"use client";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SupportedLanguage } from "@/lib/shared";
import getInitials from "@/lib/getInitials";
import { Link } from "react-router-dom";
import { localizedPath } from "@/lib/localePath";
import { Logout, Message, Profile, Setting2 } from "iconsax-react";
import lang from "./lang.json";
import { logout } from "@/actions/auth";
import SelectLanguage from "./SelectLanguage";
import { TRole } from "@/actions/types";
import { useSignalRStatus } from "@/lib/signalR/useSignalR";
import { cn } from "@/lib/utils";
import { useState } from "react";

type MobileProfileContentProps = {
  avatarSrc?: string;
  name?: string;
  locale: SupportedLanguage;
  session?: string;
  userId?: string;
  role?: TRole;
};

export default function MobileProfileContent({
  avatarSrc,
  name,
  locale,
  session,
  userId,
  role,
}: MobileProfileContentProps) {
  const { isConnected } = useSignalRStatus();

  const [isOpen, isOpenSet] = useState(false);

  const initials = name ? getInitials(name) : "U";
  return (
    <Sheet open={isOpen} onOpenChange={isOpenSet}>
      <SheetTrigger asChild>
        <button
          type="button"
          className={cn(
            "cursor-pointer w-10 aspect-square rounded-full overflow-hidden relative flex-none border",
            isConnected ? "border-green-500" : "border-red-500",
          )}
        >
          <Avatar className="w-full h-full">
            <AvatarImage src={avatarSrc} className="object-cover" />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {lang["profile-menu-title"][locale] || "Profile"}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {lang["sheet-profile-menu-desc"][locale]}
          </SheetDescription>
        </SheetHeader>

        <hr />

        <div className="flex flex-col gap-y-4 px-4 h-full pb-8">
          <Link
            onClick={() => isOpenSet((prev) => !prev)}
            to={localizedPath(locale, "/me")}
            className="flex items-center gap-3 cursor-pointer text-primary"
          >
            <Profile
              size="24"
              variant="Outline"
              className="fill-current !text-inherit"
            />
            <span className="font-medium">
              {lang["my-profile"][locale] || "My Profile"}
            </span>
          </Link>

          <Link
            onClick={() => isOpenSet((prev) => !prev)}
            to={localizedPath(locale, "/me/conversations")}
            className="flex items-center gap-3 cursor-pointer text-primary"
          >
            <Message
              size="24"
              variant="Outline"
              className="fill-current !text-inherit"
            />
            <span className="font-medium">
              {lang["messages"][locale] || "Messages"}
            </span>
          </Link>

          <Link
            onClick={() => isOpenSet((prev) => !prev)}
            to={localizedPath(locale, "/settings")}
            className="flex items-center gap-3 cursor-pointer text-primary"
          >
            <Setting2
              size="24"
              variant="Outline"
              className="fill-current !text-inherit"
            />
            <span className="font-medium">
              {lang["setting"][locale] || "Setting"}
            </span>
          </Link>
          <button
            onClick={async () => {
              isOpenSet((prev) => !prev);
              await logout();
            }}
            className="flex w-full items-center gap-3 cursor-pointer text-red-500"
          >
            <Logout
              size="24"
              className="fill-current !text-inherit"
              variant="Outline"
            />
            <span className="font-medium">
              {lang["logout"][locale] || "Logout"}
            </span>
          </button>

          {process.env.NODE_ENV === "development" && (
            <>
              {role}
              <span className="flex items-center gap-3 cursor-pointer text-primary font-medium">
                copy role
              </span>
              <button
                onClick={async () => {
                  isOpenSet((prev) => !prev);
                  navigator.clipboard.writeText(session || "");
                }}
                className="flex items-center gap-3 cursor-pointer text-primary"
              >
                <span className="font-medium">copy X-API-KEY</span>
              </button>
              <button
                onClick={async () => {
                  isOpenSet((prev) => !prev);
                  navigator.clipboard.writeText(userId || "");
                }}
                className="flex items-center gap-3 cursor-pointer text-primary"
              >
                <span className="font-medium">copy User Id</span>
              </button>
            </>
          )}

          <span className="mt-auto"></span>
          <SelectLanguage />
        </div>
      </SheetContent>
    </Sheet>
  );
}
